<template>
  <div
    class="copilot-message-bubble"
    :class="[
      `copilot-message-bubble--${message.role}`,
      `copilot-message-bubble--type-${message.contentType}`,
    ]"
  >
    <div class="copilot-message-bubble__content">
      <component
        :is="contentComponent"
        v-bind="contentProps"
      />
    </div>
  </div>
</template>

<script>
import GenerativeBlockRenderer from '@baserow/modules/copilot/components/generative/GenerativeBlockRenderer'
import MarkdownMessage from '@baserow/modules/copilot/components/chat/MarkdownMessage'
import { sanitizeMessageContent } from '@baserow/modules/copilot/utils/messageContent'

export default {
  name: 'CopilotMessageBubble',
  components: {
    GenerativeBlockRenderer,
    MarkdownMessage,
  },
  props: {
    /**
     * Message object produced by the Copilot store/service layer.
     *
     * Expected shape (simplified):
     * - id: string | number
     * - role: 'user' | 'assistant' | 'system' | 'nudge'
     * - contentType: 'markdown' | 'generative_block'
     * - content: string | GenerativeBlockPayload
     */
    message: {
      type: Object,
      required: true,
    },
  },
  computed: {
    contentComponent() {
      if (this.message.contentType === 'generative_block') {
        return 'GenerativeBlockRenderer'
      }
      return 'MarkdownMessage'
    },
    contentProps() {
      if (this.message.contentType === 'generative_block') {
        return {
          block: this.message.content,
        }
      }
      const rawContent = this.message.content
      const markdown =
        typeof rawContent === 'string' && rawContent.startsWith('copilot.')
          ? this.$t(rawContent)
          : sanitizeMessageContent(rawContent)
      return {
        markdown,
        reasoningMarkdown: this.message.reasoningMarkdown,
        streaming: this.message.streaming === true,
      }
    },
  },
}
</script>

<style scoped lang="scss">
.copilot-message-bubble {
  display: flex;
  margin-bottom: 8px;
}

.copilot-message-bubble__content {
  max-width: 72%;
}

.copilot-message-bubble--user {
  justify-content: flex-end;
}

.copilot-message-bubble--assistant,
.copilot-message-bubble--system,
.copilot-message-bubble--nudge {
  justify-content: flex-start;
}

/**
 * Minimalist bubble styling for plain text (markdown) messages.
 * Generative blocks keep their own card/table layouts.
 */
.copilot-message-bubble--type-markdown .copilot-message-bubble__content {
  display: inline-block;
  padding: 9px 12px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
}

.copilot-message-bubble--user.copilot-message-bubble--type-markdown
  .copilot-message-bubble__content {
  background: #1f2937;
  color: #ffffff;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.18);

  :deep(.markdown-message__content),
  :deep(.markdown-message__content p),
  :deep(.markdown-message__content li),
  :deep(.markdown-message__content span),
  :deep(.markdown-message__content a) {
    color: #ffffff;
  }
}

.copilot-message-bubble--assistant.copilot-message-bubble--type-markdown
  .copilot-message-bubble__content,
.copilot-message-bubble--system.copilot-message-bubble--type-markdown
  .copilot-message-bubble__content {
  background: #e5e7eb;
  color: #1f2937;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.copilot-message-bubble--nudge .markdown-message__content {
  font-size: 12px;
  color: #555;
  background: transparent;
  padding: 0;
  border-radius: 0;
}
</style>

